import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Unstable_Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectCanEditAllSales } from "../../redux/auth";
import { selectors as customerSelectors } from "../../redux/customers";
import { selectCurrentSalesAgent } from "../../redux/salesAgents";
import { selectors as supplierSelectors } from "../../redux/suppliers";
import { toDateOnlyString } from "../../utils";
import ComboBox from "../ComboBox";
import CurrencyField from "../CurrencyField";
import PaymentMethodSelectBox from "./PaymentMethodSelectBox";
import SalesAgentComboBox from "./SalesAgentComboBox";

const EditSaleForm = (props) => {
  const { children, isRefund, onChange, sale, subTitle } = props;

  const currentSalesAgent = useSelector(selectCurrentSalesAgent);

  const commission = sale?.commission || "0.00";
  const customer = sale?.customer || null;
  const descr = sale?.descr || "";
  const sup = sale?.sup || null;
  const paymentMethod = sale?.paymentMethod || "";
  const penalty = sale?.penalty || "0.00";
  const price = sale?.price || "0.00";
  const saleDate = dayjs(sale?.saleDate);
  const salesAgent = sale?.sa || currentSalesAgent || null;
  const serviceFee = sale?.serviceFee || "0.00";
  const tax = sale?.tax || "0.00";

  const onSaleChange = (changes) => {
    onChange({
      ...sale,
      sa: salesAgent,
      saleDate: toDateOnlyString(saleDate),
      ...changes,
    });
  };

  const canEditAllSales = useSelector(selectCanEditAllSales);

  const customerOptions = useSelector(customerSelectors.selectAll);

  const supplierOptions = useSelector(supplierSelectors.selectAll);

  const maxSaleDate = canEditAllSales ? null : dayjs(toDateOnlyString(dayjs()));
  const minSaleDate = canEditAllSales
    ? null
    : maxSaleDate.subtract(process.env.REACT_APP_SALES_OLD_AFTER_DAYS, "days");

  const onCommissionChange = (commission) => {
    onSaleChange({ commission });
  };

  const onCustomerChange = (value) => {
    const customer = value ? { id: value.id, name: value.name } : null;
    const customerServiceFee = value?.serviceFee;
    const changedServiceFee =
      customerServiceFee && customerServiceFee !== "0.00"
        ? customerServiceFee
        : serviceFee;
    onSaleChange({ customer, serviceFee: changedServiceFee });
  };

  const onDescriptionChange = (event) => {
    const descr = event.target.value;
    onSaleChange({ descr });
  };

  const onSupplierChange = (value) => {
    const sup = value ? { id: value.id, name: value.name } : null;
    onSaleChange({ sup });
  };

  const onPaymentMethodChange = (event) => {
    const paymentMethod = event.target.value;
    onSaleChange({ paymentMethod });
  };

  const onPenaltyChange = (penalty) => {
    onSaleChange({ penalty });
  };

  const onPriceChange = (price) => {
    onSaleChange({ price });
  };

  const onSaleDateChange = (saleDate) => {
    onSaleChange({ saleDate: toDateOnlyString(saleDate) });
  };

  const onSalesAgentChange = (value) => {
    const salesAgent = value ? { id: value.id, name: value.name } : null;
    onSaleChange({ sa: salesAgent });
  };

  const onServiceFeeChange = (serviceFee) => {
    onSaleChange({ serviceFee });
  };

  const onTaxChange = (tax) => {
    onSaleChange({ tax });
  };

  return (
    <Grid container spacing={2} sx={{ overflowX: "hidden" }}>
      <Grid xs={12}>
        <SalesAgentComboBox
          onChange={onSalesAgentChange}
          value={salesAgent}
          disabled={!canEditAllSales}
          readOnly={!canEditAllSales}
        />
      </Grid>
      <Grid xs={12}>
        <DatePicker
          format="DD-MMM-YYYY"
          label="Sale Date *"
          maxDate={maxSaleDate}
          minDate={minSaleDate}
          onChange={onSaleDateChange}
          required
          slotProps={{ textField: { fullWidth: true } }}
          value={saleDate}
        />
      </Grid>
      <Grid xs={12}>
        <Divider textAlign="left">{subTitle || "Other Information"}</Divider>
      </Grid>
      {children}
      <Grid xs={12}>
        <TextField
          fullWidth
          label="Description"
          maxRows={5}
          multiline
          onChange={onDescriptionChange}
          value={descr}
        />
      </Grid>
      <Grid xs={12}>
        <Divider textAlign="left">Customer & Supplier</Divider>
      </Grid>
      <Grid xs={12}>
        <ComboBox
          fullWidth
          getOptionLabel={(option) => option.name}
          label="Customer"
          onChange={onCustomerChange}
          options={customerOptions}
          value={customer}
        />
      </Grid>
      <Grid xs={12}>
        <ComboBox
          fullWidth
          getOptionLabel={(option) => option.name}
          label="Supplier"
          onChange={onSupplierChange}
          options={supplierOptions}
          value={sup}
        />
      </Grid>
      <Grid xs={12}>
        <Divider textAlign="left">Payment Information</Divider>
      </Grid>
      <Grid xs={12}>
        <PaymentMethodSelectBox
          onChange={onPaymentMethodChange}
          required
          value={paymentMethod}
        />
      </Grid>
      <Grid xs={6.6}>
        <CurrencyField
          fullWidth
          label="Price"
          onChange={onPriceChange}
          required
          value={price}
        />
      </Grid>
      <Grid xs={5.4}>
        <CurrencyField
          fullWidth
          label="Tax"
          onChange={onTaxChange}
          value={tax}
        />
      </Grid>
      <Grid xs={6.6}>
        <CurrencyField
          fullWidth
          label="Commission"
          onChange={onCommissionChange}
          value={commission}
        />
      </Grid>
      <Grid xs={5.4}>
        <CurrencyField
          fullWidth
          label="Service Fee"
          onChange={onServiceFeeChange}
          value={serviceFee}
        />
      </Grid>
      {isRefund && (
        <Grid xs={6.6}>
          <CurrencyField
            fullWidth
            label="Penalty"
            onChange={onPenaltyChange}
            value={penalty}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default EditSaleForm;
